/**
 * Константы предметной области и переключатели среды.
 *
 * Всё, что раньше лежало вперемешку с хуками запросов и по экранам, собрано
 * здесь: одно место, где меняют язык по умолчанию, список сайтов загрузки или
 * набор печатных каталогов справочника.
 */

/** Принадлежность группы Риге — выводится из `PriceGroupID`.
 *  Соответствует `GroupMembership` в `GroupMembershipReader.cs:56-63`. */
export const RIGA_PRICE_GROUP = 1000

/** Русский язык. Единственный, на котором ERP разрешает создание группы
 *  (`ProductGroupController.cs:122-128`), и закрытый для правки РФ-групп. */
export const RUSSIAN_LANGUAGE = 11

/**
 * Прайс-группы РФ с заметным объёмом — быстрые фильтры «по марке» под флагом
 * «РФ» на экране списка групп (запрос пользователя 25 августа). В `PriceGroups`
 * на проде под РФ-группами ~27 разных id, но два держат основную массу: id 6
 * («АвтоDело» в БД — опечатка исторически в самом названии) даёт 1329 из 2028
 * РФ-групп, id 148 («Beril») — ещё 170. Остальные ~530 групп размазаны по
 * полусотне мелких прайс-групп (города вроде «Воронеж», «Техмаш») и в отдельный
 * фильтр не выделяются — они остаются под «Все».
 */
export const FOREIGN_BRAND_PRICE_GROUPS = {
	avtodelo: 6,
	beril: 148,
} as const

/** Язык контента по умолчанию: латышский — рижская команда работает в нём чаще всего. */
export const DEFAULT_LANGUAGE = 4

/** Служебные атрибуты: сортировка, признак «не выводить», вес.
 *  Колонками не показываются — `Constants.cs:15,20,25`. */
export const HIDDEN_ATTRIBUTES = [0, 1, 57]

/** `CatalogGroup.Web` — дерево категорий сайта, корень `2000`.
 *  Оно общее для РФ и Риги. Второе веб-дерево, `Web_Latvia` (`3`, корень `4000`),
 *  на проде пустое: одна категория `Web_Latvia_inner` и ни одной привязки,
 *  поэтому в интерфейсе его нет. Печатный каталог (`1`) Риге не показываем. */
export const WEB_CATALOG_GROUP = 0

/** Псевдокаталог «Вне каталогов»: `catalogId` не передаётся вовсе, и бэк уходит
 *  в `GetTranslatesForNotDistributedGroups` — фразы групп, не попавших ни в один
 *  каталог (`TranslateReader.cs:156-157`). Ноль здесь наш, в ERP такого id нет. */
export const NO_CATALOG = 0

/**
 * Сайты, доступные на экране загрузки.
 *
 * Список короткий намеренно: заливка чистит таблицы сайта целиком, и промах в
 * выпадающем списке стоит дорого. Расширять — добавлять имя в массив; имена
 * совпадают с `WebSites.SiteName` один в один.
 *
 * `avtodelo.bg` — тот, на котором механизм обкатан впервые. `avtodelo.it` —
 * целевой сайт тестов, заведён 25 августа (`WebSites.ID = 19`, `Catalogs.ID =
 * 37`, см. `САЙТ-ИТАЛИЯ.md`). `avtodelo.lv` (`WebSites.ID = 2`, `catalogId=10`,
 * см. `API-CONTRACT.md`) — настоящий рабочий сайт Риги, добавлен в список 25
 * августа по прямой просьбе. Все три входят в `Publish:AllowedWebsiteIds`
 * рижского BFF (`2,15,19` — см. `PLAN.md`, запись 25 августа); без этого
 * `/ai/site/*` отвечает `403` тем же текстом, что и список разрешённых.
 *
 * Справочника переводов это не касается: там свой список сайтов Риги, он нужен
 * для ссылок на товар и остаётся полным.
 */
export const PUBLISH_SITES = ["www.avtodelo.it", "www.avtodelo.bg", "www.avtodelo.lv"]

/**
 * Печатные каталоги, которые Риге нужны в фильтре справочника переводов.
 * Остальные печатные и все чужие сайты из фильтра убраны — по решению заказчика.
 *
 * Сопоставляем по имени, а не по id: имена совпадают со строками в ERP, а id
 * стенда и прода расходиться могут. Если каталог переименуют, он пропадёт из
 * списка — тогда поправить здесь.
 */
export const PRINTED_CATALOGS = [
	"Каталог BERIL",
	"Каталог Автодело 2025",
	"Новинки 2024 Автодело",
	"Тест_А Каталог 2026",
]

/**
 * Доступен ли из браузера рижский BFF — тот, кто ходит в базы сайтов.
 *
 * Правило проекта: **в базы сайтов Риги ходит только рижский узел**. Через
 * Россию это не работает и работать не будет: методы `ecm`
 * (`is-on-site`, `add-group`, `remove-group`, `fill-website-table`,
 * `partial-refresh`, `translate/with-urls`) открывают соединение с MySQL сайта
 * из российского контейнера, где оно не проходит, а `MySqlException` прилетает
 * из таймера драйвера мимо обработчиков и **роняет процесс `ecm` целиком**.
 * Проверено 24 августа на живом проде: два нажатия «Проверить на сайте» — два
 * перезапуска ERP (`502` через 15,8 с, время старта процесса в `HangFire.Server`
 * сдвинулось следом). При этом с самого рижского узла та же база отвечает за
 * 0,7 с.
 *
 * На узле фронт собран (`import.meta.env.PROD`) и `/ai/*` идёт в свой же nginx.
 * На стенде рижского BFF нет: адрес задаётся `RIGA_AI_ORIGIN` в
 * `frontend/.env.local`, и тогда vite проксирует `/ai` туда. Осознанно: узел
 * работает с **прод**-данными и живым сайтом, а токен стенда его `TokenGuard`
 * не примет — проверять надо с прод-логином (`RIGA_API_ORIGIN`).
 */
export const SITE_API_ENABLED =
	import.meta.env.PROD || !!import.meta.env.RIGA_AI_ORIGIN

/** Один текст на все места, где кнопка выключена этим правилом. */
export const SITE_API_NOTE =
	"Операции с базой сайта идут в рижский BFF (`/ai/site/*`), а его на локальном стенде нет. Включить: RIGA_AI_ORIGIN=https://riga.avtodelo-erp.ru в riga-app/frontend/.env.local — учтите, что узел работает с прод-данными и живым сайтом."

/**
 * Что до сих пор ходит в базу сайта через Россию — и потому выключено.
 *
 * Точечное обновление и ссылки на товар в справочнике живут только в `ecm`;
 * своего метода на рижском узле у них пока нет. Нажатие не «не сработает», а
 * положит весь российский ERP, поэтому кнопки погашены до появления рижских
 * методов (`PLAN.md`, фаза 7-Л). Массовую заливку таблиц (`fill-website-table`)
 * той же участи убрали вместе с интерфейсом — версии для Риги так и не
 * появилось, а кнопка без неё была витриной.
 */
export const VIA_RU_NOTE =
	"Выключено: этот метод есть только в российском ecm, а он ходит в базу сайта из России — соединение не проходит, и процесс ecm падает вместе со всем ERP. Ждёт своего метода на рижском узле."
